import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/Checkbox'
import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import type {
  Capability,
  CapabilityCatalogue,
  RoleChange,
  RoleRecord,
  ScopeLevel,
} from '@/types/domain'

/* The role editor — what a role is called, and what it may do.
 *
 * Two panels, because authorization here has two orthogonal axes and collapsing
 * them into one list is the mistake that makes a role screen lie:
 *
 *   CAPABILITIES — what the holder may DO. "May issue an invoice."
 *   ROW SCOPE    — whose records they may SEE. "Every case, or only their own."
 *
 * A role with `document.invoice.issue` and no `cases` scope can issue invoices
 * against nothing; a role with `all` on cases and no capabilities can read
 * everything and change none of it. Both are legitimate, and neither is
 * expressible if the screen only has checkboxes.
 *
 * The scope selector's fourth option is the important one. "No access" is not
 * the same as "own" — it means the surface is unreachable rather than filtered
 * — and it is the DEFAULT for a new role, because a role starts able to reach
 * nothing until somebody says otherwise.
 */

interface RoleFormSheetProps {
  open: boolean
  onClose: () => void
  /** The role being edited, or undefined when creating one. */
  role?: RoleRecord
  catalogue: CapabilityCatalogue
  onSubmit: (change: RoleChange) => void
  pending?: boolean
  error?: string
}

/** The wire value for "this role does not reach this surface at all".
 *
 * An empty string rather than a level, because absence is not a level: the
 * server stores no row, and a row saying `none` would be a fourth level every
 * lookup would have to handle. The submit step drops these keys entirely. */
const NO_ACCESS = ''

export function RoleFormSheet({
  open,
  onClose,
  role,
  catalogue,
  onSubmit,
  pending,
  error,
}: RoleFormSheetProps) {
  const { t } = useTranslation()
  const editing = role !== undefined

  const [key, setKey] = useState(role?.key ?? '')
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [capabilities, setCapabilities] = useState<Set<Capability>>(
    () => new Set(role?.capabilities ?? []),
  )
  const [scopes, setScopes] = useState<Record<string, string>>(() => ({ ...(role?.scopes ?? {}) }))

  /* Capabilities grouped by their first dotted segment — quote.*, case.*,
   * directory.* — so the editor reads as sections rather than as forty
   * checkboxes in registration order. The grouping is derived from the KEY
   * rather than from a hand-kept list, so a capability added server-side lands
   * in the right section without this file changing. */
  const groups = useMemo(() => {
    const byPrefix = new Map<string, typeof catalogue.items>()
    for (const item of catalogue.items) {
      const prefix = item.key.split('.')[0] ?? 'other'
      const bucket = byPrefix.get(prefix) ?? []
      bucket.push(item)
      byPrefix.set(prefix, bucket)
    }
    return [...byPrefix.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [catalogue.items])

  function toggle(capability: Capability) {
    setCapabilities((current) => {
      const next = new Set(current)
      if (next.has(capability)) next.delete(capability)
      else next.add(capability)
      return next
    })
  }

  function submit() {
    // Surfaces set to "no access" are DROPPED rather than sent as a level. A
    // missing key is how the server stores "unreachable"; sending one would
    // either be rejected or, worse, stored as the narrowest real level.
    const chosen: Record<string, ScopeLevel> = {}
    for (const [surface, level] of Object.entries(scopes)) {
      if (level !== NO_ACCESS) chosen[surface] = level as ScopeLevel
    }

    onSubmit({
      key: key.trim(),
      name: name.trim(),
      description: description.trim(),
      capabilities: [...capabilities],
      scopes: chosen,
    })
  }

  /* The superuser's capability list is not editable, because the flag rather
   * than the list is what grants access — an edit would be a no-op that looked
   * like it worked. The server refuses it; the form says so rather than
   * offering forty checkboxes that would be silently discarded. */
  const superuserLocked = role?.isSuperuser ?? false

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={editing ? t('settings.roles.edit', { name: role.name }) : t('settings.roles.create')}
      onSubmit={submit}
      pending={pending}
      disabled={key.trim() === '' || name.trim() === ''}
      error={error}
      notice={superuserLocked ? t('settings.roles.superuserLocked') : undefined}
    >
      <Field label={t('settings.roles.fields.key')} hint={t('settings.roles.fields.keyHint')} required>
        {(props) => (
          <Input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            /* A system role's key is fixed: renaming `sales` would break every
               translation and fixture that names it, and the server refuses
               it. Disabled rather than absent so the founder can still SEE the
               key they are editing. */
            disabled={role?.isSystem ?? false}
            placeholder="ops-lead"
            {...props}
          />
        )}
      </Field>

      <Field label={t('settings.roles.fields.name')} required>
        {(props) => (
          <Input value={name} onChange={(e) => setName(e.target.value)} {...props} />
        )}
      </Field>

      <Field
        label={t('settings.roles.fields.description')}
        hint={t('settings.roles.fields.descriptionHint')}
      >
        {(props) => (
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            {...props}
          />
        )}
      </Field>

      {/* ── row scope ───────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium text-text-heading">
            {t('settings.roles.scopes.title')}
          </h3>
          <p className="text-xs text-text-muted">{t('settings.roles.scopes.hint')}</p>
        </div>

        {catalogue.surfaces.map((surface) => (
          <Field key={surface} label={t(`settings.roles.surfaces.${surface}` as never, surface)}>
            {(props) => (
              <Select
                value={scopes[surface] ?? NO_ACCESS}
                onChange={(value) => setScopes((current) => ({ ...current, [surface]: value }))}
                options={[
                  // Listed FIRST and the default for an unset surface: a role
                  // reaches nothing until somebody grants it.
                  { value: NO_ACCESS, label: t('settings.roles.levels.none') },
                  ...catalogue.levels.map((level) => ({
                    value: level,
                    label: t(`settings.roles.levels.${level}` as never, level),
                  })),
                ]}
                {...props}
              />
            )}
          </Field>
        ))}
      </section>

      {/* ── capabilities ────────────────────────────────────────────────── */}
      {!superuserLocked && (
        <section className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-text-heading">
              {t('settings.roles.capabilities.title')}
            </h3>
            <p className="text-xs text-text-muted">{t('settings.roles.capabilities.hint')}</p>
          </div>

          {groups.map(([prefix, items]) => (
            <fieldset key={prefix} className="space-y-2">
              <legend className="text-xs font-medium uppercase tracking-wide text-text-muted">
                {t(`settings.roles.groups.${prefix}` as never, prefix)}
              </legend>
              {items.map((item) => (
                <Checkbox
                  key={item.key}
                  label={item.description}
                  hint={item.key}
                  checked={capabilities.has(item.key)}
                  onCheckedChange={() => toggle(item.key)}
                />
              ))}
            </fieldset>
          ))}
        </section>
      )}
    </FormSheet>
  )
}
